import { useState, useEffect } from 'react';
import type { Anbiyam, AnbiyamPayload } from '@/api/anbiyam';
import { validatePhone, validateEmail } from '@/utils/validation';

function FieldError({ msg }: { msg: string }) {
  return msg ? <p className="text-red-500 text-xs mt-0.5">{msg}</p> : null;
}

interface Props {
  initial?: Anbiyam | null;   // null = create mode
  onSave:  (payload: AnbiyamPayload) => Promise<void>;
  onClose: () => void;
}

const ZONES = [1, 2, 3, 4, 5, 6, 7, 8];

export default function AnbiyamFormModal({ initial, onSave, onClose }: Props) {
  const isEdit = Boolean(initial);

  const [anbiyamName,      setAnbiyamName]      = useState(initial?.anbiyamName      ?? '');
  const [anbiyamCode,      setAnbiyamCode]       = useState(initial?.anbiyamCode      ?? '');
  const [zone,             setZone]              = useState<number>(initial?.zone       ?? 1);
  const [coordinatorName,    setCoordinatorName]    = useState(initial?.coordinatorName    ?? '');
  const [coordinatorPhone,   setCoordinatorPhone]   = useState(initial?.coordinatorPhone   ?? '');
  const [assCoordinatorName, setAssCoordinatorName] = useState(initial?.assCoordinatorName ?? '');
  const [coordinatorEmail,   setCoordinatorEmail]   = useState(initial?.coordinatorEmail   ?? '');
  const [error,              setError]              = useState('');
  const [saving,           setSaving]            = useState(false);
  const [phoneErr,         setPhoneErr]          = useState('');
  const [emailErr,         setEmailErr]          = useState('');

  // Auto-derive code from name (first 2 chars, uppercase) — create mode only
  const handleNameBlur = () => {
    if (!isEdit && anbiyamName.trim() && !anbiyamCode.trim()) {
      setAnbiyamCode(anbiyamName.trim().slice(0, 2).toUpperCase());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const pErr = validatePhone(coordinatorPhone);
    const eErr = validateEmail(coordinatorEmail);
    setPhoneErr(pErr);
    setEmailErr(eErr);

    if (!anbiyamName.trim())        { setError('Anbiyam name is required');        return; }
    if (!zone)                       { setError('Zone is required');                return; }
    if (!coordinatorName.trim())     { setError('Coordinator name is required');    return; }
    if (!coordinatorPhone.trim())    { setError('Coordinator phone is required');   return; }
    if (pErr || eErr)                { setError('Please fix the highlighted fields.'); return; }

    const payload: AnbiyamPayload = {
      anbiyamName:        anbiyamName.trim(),
      anbiyamCode:        anbiyamCode.trim() || undefined,
      zone,
      coordinatorName:    coordinatorName.trim(),
      coordinatorPhone:   coordinatorPhone.trim(),
      assCoordinatorName: assCoordinatorName.trim() || undefined,
      coordinatorEmail:   coordinatorEmail.trim()   || undefined,
    };

    setSaving(true);
    try {
      await onSave(payload);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grid-border">
          <h2 className="text-base font-semibold text-navy font-georgia">
            {isEdit ? 'Edit Anbiyam' : 'Add Anbiyam'}
          </h2>
          <button
            onClick={onClose}
            className="text-teal hover:text-navy text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <form id="anbiyam-form" onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded px-3 py-2">
              {error}
            </div>
          )}

          {/* Anbiyam Name */}
          <div>
            <label className="label">Anbiyam Name {isEdit && <span className="text-gray-400 text-xs">(read-only)</span>}</label>
            <input
              type="text"
              className="input-field"
              value={anbiyamName}
              onChange={(e) => setAnbiyamName(e.target.value)}
              onBlur={handleNameBlur}
              readOnly={isEdit}
              disabled={isEdit}
              required
            />
          </div>

          {/* Anbiyam Code */}
          <div>
            <label className="label">Anbiyam Code {isEdit && <span className="text-gray-400 text-xs">(read-only)</span>}</label>
            <input
              type="text"
              className="input-field"
              value={anbiyamCode}
              onChange={(e) => setAnbiyamCode(e.target.value.toUpperCase())}
              readOnly={isEdit}
              disabled={isEdit}
              maxLength={10}
              placeholder="Auto-derived from name"
            />
          </div>

          {/* Zone */}
          <div>
            <label className="label">Zone</label>
            <select
              className="input-field"
              value={zone}
              onChange={(e) => setZone(Number(e.target.value))}
              required
            >
              {ZONES.map((z) => (
                <option key={z} value={z}>Zone {z}</option>
              ))}
            </select>
          </div>

          {/* Coordinator Name */}
          <div>
            <label className="label">Coordinator Name</label>
            <input
              type="text"
              className="input-field"
              value={coordinatorName}
              onChange={(e) => setCoordinatorName(e.target.value)}
              required
            />
          </div>

          {/* Coordinator Phone */}
          <div>
            <label className="label">Coordinator Phone</label>
            <input
              type="tel"
              className={`input-field ${phoneErr ? 'border-red-400' : ''}`}
              value={coordinatorPhone}
              onChange={(e) => { setCoordinatorPhone(e.target.value); if (phoneErr) setPhoneErr(validatePhone(e.target.value)); }}
              onBlur={(e) => setPhoneErr(validatePhone(e.target.value))}
              placeholder="10-digit mobile"
              required
            />
            <FieldError msg={phoneErr} />
          </div>

          {/* Coordinator Email (optional) */}
          <div>
            <label className="label">Coordinator Email <span className="text-gray-400 text-xs">(optional)</span></label>
            <input
              type="text"
              className={`input-field ${emailErr ? 'border-red-400' : ''}`}
              value={coordinatorEmail}
              onChange={(e) => { setCoordinatorEmail(e.target.value); if (emailErr) setEmailErr(validateEmail(e.target.value)); }}
              onBlur={(e) => setEmailErr(validateEmail(e.target.value))}
              placeholder="user@example.com"
            />
            <FieldError msg={emailErr} />
          </div>

          {/* Assistant Coordinator (optional) */}
          <div>
            <label className="label">Asst. Coordinator Name <span className="text-gray-400 text-xs">(optional)</span></label>
            <input
              type="text"
              className="input-field"
              value={assCoordinatorName}
              onChange={(e) => setAssCoordinatorName(e.target.value)}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-grid-border">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            form="anbiyam-form"
            disabled={saving}
            className="btn-primary disabled:opacity-50"
          >
            {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
